import type { HomeSettingsRW } from "building-types/settings/homeSettings";
import type { SettingsFragment } from "building-types/settings/settingsFragment";
import { type SettingsSet } from "building-types/settings/settingsSet";
import { SettingsType } from "building-types/settings/settingsType";
import type { RWTable, Table } from "common/data/table";
import { isObject } from "common/utils/isObject";
import { mergeSettingsSet } from "../mergeSettingsSet";
import { entityIsGlobalSettingsSet } from "../settingsTools";
import {
  convertSettingsFragmentToProvenance,
  getCachedHomeSettingsProvenance,
  getOrCreateWeakMap,
  provenanceCacheKeys,
  type SettingsFieldProvenance,
  type SettingsFragmentProvenance,
} from "./deriveSettingsProvenance";
import { evaluateActiveSettingsSets, type FlattenSettingsSetOptions } from "./flattenSettingsSets";

/**
 * Electrical settings are executable nested entities inside settings fragments whose
 * generated output must carry doc handles; every other settings type only needs merged
 * values at generation time, and only needs provenance when the UI inspects inheritance.
 *
 * `generateElectrical` applies merged `settings.electrical.rules` while building the
 * model. Each applied rule stamps generated groups with an `ElectricalRuleHandle` that
 * must point at the winning settings fragment (`settingsHandle.id`), not just the rule
 * id. After settings-set merge, the merged rule value alone does not record which
 * fragment won when multiple sets define the same rule id.
 *
 * Other settings types consume merged scalar/catalog values during generation and defer
 * full provenance to inspector and catalog paths via `deriveHomeSettingsProvenance`.
 * Electrical is the only generation stage that needs provenance up front, so this module
 * derives just the `electrical` field instead of converting and merging every settings
 * type. Results match `deriveHomeSettingsProvenance(props).electrical`.
 */

const electricalSettingsProvenanceCache = new WeakMap<
  object,
  WeakMap<object, WeakMap<object, WeakMap<object, WeakMap<object, SettingsFieldProvenance>>>>
>();

function getCachedElectricalSettingsProvenance(props: FlattenSettingsSetOptions): SettingsFieldProvenance | undefined {
  const { settings, settingsSets, optionChoices, optionSets, entityDisabledByGroup } = provenanceCacheKeys(props);
  return electricalSettingsProvenanceCache
    .get(settings)
    ?.get(settingsSets)
    ?.get(optionChoices)
    ?.get(optionSets)
    ?.get(entityDisabledByGroup);
}

function setCachedElectricalSettingsProvenance(
  props: FlattenSettingsSetOptions,
  provenance: SettingsFieldProvenance,
): SettingsFieldProvenance {
  const { settings, settingsSets, optionChoices, optionSets, entityDisabledByGroup } = provenanceCacheKeys(props);
  const bySettingsSets = getOrCreateWeakMap(electricalSettingsProvenanceCache, settings, () => new WeakMap());
  const byOptionChoices = getOrCreateWeakMap(bySettingsSets, settingsSets, () => new WeakMap());
  const byOptionSets = getOrCreateWeakMap(byOptionChoices, optionChoices, () => new WeakMap());
  const byDisabled = getOrCreateWeakMap(byOptionSets, optionSets, () => new WeakMap());
  byDisabled.set(entityDisabledByGroup, provenance);
  return provenance;
}

function convertElectricalSettingsTableToProvenance(
  settings: Table<SettingsFragment>,
): RWTable<SettingsFragmentProvenance> {
  const settingsTableAsProvenance: RWTable<SettingsFragmentProvenance> = {};
  for (const settingsId in settings) {
    const settingsFragment = settings[settingsId];
    if (settingsFragment.type === SettingsType.ELECTRICAL_SETTINGS) {
      settingsTableAsProvenance[settingsId] = convertSettingsFragmentToProvenance(settingsFragment);
    }
  }
  return settingsTableAsProvenance;
}

function mergeElectricalProvenanceFromSortedSets(
  sortedSets: SettingsSet[],
  settings: FlattenSettingsSetOptions["settings"],
): SettingsFragmentProvenance {
  const homeSettingsProvenance = {} as HomeSettingsRW;
  // Scoped to ELECTRICAL_SETTINGS here; deriveSettingsProvenance converts the full table
  // before mergeProvenanceFromSortedSets instead.
  const electricalSettingsAsProvenance = convertElectricalSettingsTableToProvenance(settings);
  for (const settingsSet of sortedSets) {
    if (!settingsSet.type || entityIsGlobalSettingsSet(settingsSet)) {
      mergeSettingsSet(
        homeSettingsProvenance,
        settingsSet.id,
        electricalSettingsAsProvenance as unknown as FlattenSettingsSetOptions["settings"],
      );
    }
  }
  const electricalProvenance = (homeSettingsProvenance as unknown as SettingsFragmentProvenance).electrical;
  return isObject(electricalProvenance) ? electricalProvenance : {};
}

/**
 * Provenance for the merged `electrical` home-settings field, keyed like
 * `deriveHomeSettingsProvenance` / `flattenSettingsSetsWithProvenance` (full stack,
 * no `entityDisabledByGroup`, identity-cached). Callers typically read
 * `provenance.rules[ruleId]` to build `ElectricalRuleHandle.settingsHandle`. Returns an empty
 * object when no electrical settings fragments were merged.
 */
export function deriveElectricalSettingsProvenance(props: FlattenSettingsSetOptions): SettingsFieldProvenance {
  const fullCached = getCachedHomeSettingsProvenance(props);
  if (fullCached?.electrical !== undefined) {
    return fullCached.electrical;
  }

  const cached = getCachedElectricalSettingsProvenance(props);
  if (cached) {
    return cached;
  }

  const electricalProvenance = mergeElectricalProvenanceFromSortedSets(
    evaluateActiveSettingsSets(props),
    props.settings,
  );
  return setCachedElectricalSettingsProvenance(props, electricalProvenance);
}

export function electricalRuleProvenanceSettingsId(
  electricalSettingsProvenance: SettingsFieldProvenance,
  ruleId: string,
): string | undefined {
  if (!isObject(electricalSettingsProvenance)) {
    return undefined;
  }
  const rulesProvenance = electricalSettingsProvenance.rules;
  if (!isObject(rulesProvenance)) {
    return undefined;
  }
  const settingsId = rulesProvenance[ruleId];
  return typeof settingsId === "string" ? settingsId : undefined;
}
